import { isValid } from 'date-fns';
import * as EmailValidator from 'email-validator';
import { validateNorwegianIdNumber } from 'norwegian-national-id-validator';

import { parseDateInput } from '@eika/date-input/lib/utils/dateParser';

import erGyldigKontonummer from '../../norsk-validator/erGyldigKontonummer';
import erGyldigOrganisasjonsnummer from '../../norsk-validator/erGyldigOrganisasjonsnummer';
import {
    ForsikringData,
    isForsikringIngenAlternativerType,
    isForsikringListeType,
} from '../skadedato-and-forsikring/skadedato-and-forsikring-types';

const MOBIL_REGEX = /^(4|9)+\d{7}$/;
const TELEFONNUMMER_REGEX = /\d{8}$/;

export const MAKSLENGDER = {
    fornavn: 100,
    etternavn: 100,
    epost: 150,
    lokasjon: 500,
    organisasjonsnummer: 9,
    fodselsnummer: 11,
    melding: 20000,
    registreringsnummer: 100,
    kontonummer: 11,
    forsikringsnummer: 11,
    inputTekst: 150,
};

export const validateIsNonEmptyText = (tekst?: string) => {
    return tekst !== undefined && tekst.length !== 0;
};

export const validateInputLengde = (lengde: number, tekst = '') => {
    return tekst.length <= lengde;
};

export const validateMobilnummer = (tlf?: string) => {
    return MOBIL_REGEX.test(tlf);
};

export const validateTelefonnummer = (tlf?: string) => {
    return TELEFONNUMMER_REGEX.test(tlf) && tlf.length === 8;
};

export const validateEpost = (epost?: string) => EmailValidator.validate(epost);

export const isEmptyOrUndefined = (value?: string) => {
    return !value;
};

export const validateOrganisasjonsnummer = (orgnr: string): boolean =>
    orgnr != null && erGyldigOrganisasjonsnummer(orgnr);

export const validateFodselsnummer = (fnr: string): boolean => fnr != null && validateNorwegianIdNumber(fnr);

export const validatePostnummer = (postnummer?: string) => {
    return /^\d{4}$/.test(postnummer);
};

export const validateSkadedatoAndForsikringSporsmal = (
    skadedato: string | undefined,
    forsikringData: ForsikringData,
): boolean =>
    !!skadedato &&
    ((isForsikringListeType(forsikringData) && forsikringData.selectedAlternativ !== undefined) ||
        isForsikringIngenAlternativerType(forsikringData));

const defaultBirthDateFormat = 'dd.MM.yyyy';
export const isValidFodselsdato = (date: string, format: string = defaultBirthDateFormat): boolean => {
    try {
        const parsedDate: Date = parseDateInput(date, format);
        const oldestPerson = new Date(Date.now());
        oldestPerson.setFullYear(oldestPerson.getFullYear() - 122);

        return (
            isValid(parsedDate) &&
            parsedDate.getFullYear() >= oldestPerson.getFullYear() &&
            parsedDate < new Date(Date.now())
        );
    } catch (e) {
        return false;
    }
};

const isNumber = (number: string): boolean => {
    return /^\d+$/.test(number);
};

export const validateForsikringnummer = (forsikringsnr?: string) => {
    if (isEmptyOrUndefined(forsikringsnr)) {
        return false;
    }

    const MAXIMUM_LENGTH = MAKSLENGDER.forsikringsnummer;
    const MINIMUM_LENGTH = 1;
    return forsikringsnr.length >= MINIMUM_LENGTH && forsikringsnr.length <= MAXIMUM_LENGTH && isNumber(forsikringsnr);
};

export const isInputTooLong = (tekst?: string): boolean => tekst.length > MAKSLENGDER.inputTekst;

export const isInputForSmallTextInputSizesValid = (tekst?: string): boolean =>
    !isEmptyOrUndefined(tekst) && !isInputTooLong(tekst);

export const validateBankkontonummer = (kontonummer: string) => {
    return erGyldigKontonummer(kontonummer);
};

export const validateSkadelopenummer = (skadelopenummer: string) => {
    return /^\d{1,5}$/.test(skadelopenummer);
};

export const isPersonskadeForsikring = (skadenummer: string): boolean => {
    const personskadeForsikringPrefix = '7';
    return skadenummer[0] === personskadeForsikringPrefix;
};

export const validateSkadenummer = (skadenummer: string): boolean => {
    if (!skadenummer) {
        return false;
    }

    const [forsikringsnummer, skadelopenummer] = skadenummer.split('.');
    const isValidSkadenummer = validateForsikringnummer(forsikringsnummer) && validateSkadelopenummer(skadelopenummer);

    if (isValidSkadenummer) {
        return true;
    }

    if (isPersonskadeForsikring(skadenummer)) {
        return validateForsikringnummer(forsikringsnummer);
    }

    return false;
};
